import BoxKit from "@/shared/components/kit/box";
import CustomButton from "@/shared/components/kit/button";
import StackKit from "@/shared/components/kit/stack";
import { TextField, useTheme, useMediaQuery } from "@mui/material";
import React, { useEffect, useState } from "react";
import TableWallet from "./table";
import CustomSelect from "@/shared/components/common/select";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import { useQuery } from "@tanstack/react-query";
import { fetchWalletReport } from "@/api/user/home";
import moment from "jalali-moment";
import ContentSkeleton from "./content.skeleton";
import { BalanceData } from "@/types/api/user/home.type.data";
import { numberSeparator, toPersianNumber } from "@/hooks/useThousandsSeparator";
import { useLocation } from "react-router";
import { deriveTotal } from "@/utils/pagination";
import { normalizeTransactions } from "@/utils/normalizeTransactions";

/**
 * ContentLoan — loan transactions view.
 *
 * Uses its own `loan_page` for pagination. The wallet section is not
 * rendered here, so `wallet_page` is pinned to 1.
 */

const ContentLoan = ({ dataBalance }: { dataBalance: BalanceData | undefined }) => {
  const location = useLocation();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

  const refTypeMap: Record<string, string> = {
    transfer: "انتقال",
    order: "سفارش",
    "pro-ref-earn": "کارمزد چند سطحی حرفه ای",
    "ref-earn": "کارمزد تک سطحی",
    withdraw: "برداشت",
    installments: "اقساط",
    deposite: "افزایش موجودی",
    creditScoreFee: "کارمزد اعتبار سنجی",
    resellerOrder: "-",
    bistCard: "بیست کارت",
    edit: "ویرایش",
    "pos-pazirande": "دستگاه پوز",
    "pos-ghishe": "-",
    wallet: "کیف پول",
    ipg: "Ipg",
    "edit-pazirande": "اصلاح توسط پذیرنده",
    gift_fee: "-",
    emv: "خرید emv",
    qr_code: "QR Code",
    loam: "وام",
  };

  const [loanPage, setLoanPage] = useState(1);
  const [size, setSize] = useState(10);
  const pathname = typeof window !== "undefined" ? location.pathname : "";

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["contentLoan", pathname, loanPage, size,"loan"],
    queryFn: () => fetchWalletReport(1, loanPage, size,'loan'),
  });

  useEffect(() => {
    refetch();
  }, [pathname]);

  const dataHeader = [
    { id: "ts", title: "تاریخ و ساعت" },
    { id: "amount", title: "مبلغ ( ریال)" },
    { id: "ref_type", title: "نوع" },
    { id: "ref_id", title: "شماره تراکنش" },
    { id: "source", title: "مبدا تراکنش" },
    { id: "destination", title: "مقصد تراکنش" },
    { id: "ref_type_title", title: "وضعیت تراکنش" },
    { id: "description", title: "توضیحات" },
  ];

const rows = normalizeTransactions(data?.data);

  const newData = rows.map((item, index: number) => ({
    ...item,
    ts: toPersianNumber(
    `${moment.unix(item.ts).locale("fa").format("HH:mm")} ${moment
        .unix(item.ts)
        .locale("fa")
        .format("jYYYY/jMM/jDD")}`
    ),
    amount: (
      <TypoGraphyKit sx={{ direction: "ltr" }}>
        {numberSeparator(item.amount)}
      </TypoGraphyKit>
    ),
    ref_id: toPersianNumber(item.ref_id),
    source: toPersianNumber(item.source),
    destination: toPersianNumber(item.destination),
    ref_type: refTypeMap[item.ref_type] || item.ref_type,
    ref_type_title: (
      <TypoGraphyKit variant="body1">{item.ref_type_title}</TypoGraphyKit>
    ),
  }));

  const handlePageChange = (newPage: number) => setLoanPage(newPage);

  const handleItemsPerPageChange = (newSize: number) => {
    setSize(newSize);
    setLoanPage(1);
  };

  return (
    <>
      {isLoading ? (
        <ContentSkeleton />
      ) : (
        <StackKit
          direction="column"
          sx={{
            background:
              "linear-gradient(112.89deg, rgba(0, 0, 0, 0.1) -15.04%, rgba(134, 134, 134, 0.235528) 30.35%, rgba(115, 103, 240, 0.26) 72.13%, rgba(0, 0, 0, 0.5) 118.94%)",
            width: "100%",
            borderRadius: "8px",
          }}
        >
          <TableWallet
            dataHeader={dataHeader}
            dataTbody={newData}
            CustomSelectOption={[5, 10, 15, 20]}
            pagination={{
              current_page: loanPage,
              per_page: size,
              total: deriveTotal(loanPage, size, rows.length),
            }}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        </StackKit>
      )}
    </>
  );
};

export default ContentLoan;